/**
 * Shared parts of the November 2026 redesign (pages.html section 00): the one status chip, plain chips, kitchen
 * thumbnails, the switch, a QR code, the phone app download card. Styles in styles/shell.css (`w-` classes).
 */
import { VideoCameraIcon } from '@phosphor-icons/react'
import type { Status, StatusTone } from '@robotensor/api'
import QRCode from 'qrcode'
import { useEffect, useState, type ReactElement, type ReactNode } from 'react'
import { CONTRIBUTE } from '../lib/wallet.ts'

/** Pages on Horizon's public site, the same origin as this app (robotensor.ai has no /download or /docs). */
export const APP_URL = `${CONTRIBUTE}/download`
export const GUIDE_URL = `${CONTRIBUTE}/docs`
export const DASHBOARD_URL = `${CONTRIBUTE}/dashboard`

export type ChipTone = StatusTone | 'new' | 'plain' | 'on'

export function Chip({ tone = 'plain', children, big, className }: { tone?: ChipTone; children: ReactNode; big?: boolean; className?: string }): ReactElement {
  return (
    <span className={`w-st${className ? ` ${className}` : ''}`} data-tone={tone} data-big={big ? '' : undefined}>
      {children}
    </span>
  )
}

/** The status chip: Checking · Accepted +pts · Practice · reason · Needs a fix · Not accepted. */
export function StatusChip({ s, short, big }: { s: Status | null; short?: boolean; big?: boolean }): ReactElement | null {
  if (!s) return null
  const text = short && s.key === 'accepted' ? 'Accepted' : s.label
  return (
    <Chip tone={s.tone} big={big}>
      {text}
    </Chip>
  )
}

/** A tag (area, time, points) or a filter chip when `onClick` is set. */
export function Tag({ on, children, onClick, label }: { on?: boolean; children: ReactNode; onClick?: () => void; label?: string }): ReactElement {
  if (!onClick)
    return (
      <span className="w-tag" data-on={on ? '' : undefined}>
        {children}
      </span>
    )
  return (
    <button type="button" className="w-tag" data-on={on ? '' : undefined} aria-pressed={!!on} aria-label={label} onClick={onClick}>
      {children}
    </button>
  )
}

const TONES = ['k1', 'k2', 'k3', 'k4', 'k5'] as const

function toneOf(seed: string): (typeof TONES)[number] {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return TONES[h % TONES.length]!
}

/** A kitchen-toned thumbnail (no photos until tasks have their own pictures), with an optional corner chip.
 * `record`: a recording request or recording without a picture of its own gets the recording cover (a camera in a
 * viewfinder, in the Recording colour) instead of a pretend kitchen. */
export function Thumb({ seed, src, chip, video, record, className, alt = '' }: { seed: string; src?: string | null; chip?: ReactNode; video?: boolean; record?: boolean; className?: string; alt?: string }): ReactElement {
  const cover = record && !src
  return (
    <span className={`w-th${cover ? ' w-th--rec' : ''}${className ? ` ${className}` : ''}`} data-k={cover ? undefined : toneOf(seed)} data-video={video && !cover ? '' : undefined}>
      {src ? <img src={src} alt={alt} loading="lazy" /> : null}
      {cover ? (
        <span className="w-th__rec" aria-hidden>
          <VideoCameraIcon weight="fill" />
        </span>
      ) : null}
      {chip ? <span className="w-th__chip">{chip}</span> : null}
    </span>
  )
}

export function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (on: boolean) => void; label: string; disabled?: boolean }): ReactElement {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className="w-toggle" data-on={on ? '' : undefined} disabled={disabled} onClick={() => onChange(!on)} />
  )
}

export function Qr({ value, size = 140, label }: { value: string; size?: number; label: string }): ReactElement {
  const [src, setSrc] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    void QRCode.toDataURL(value, { margin: 1, width: size * 2, color: { dark: '#1F1712', light: '#ffffff' } })
      .then((s) => alive && setSrc(s))
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [value, size])
  return <span className="w-qr" style={{ width: size, height: size }}>{src ? <img src={src} alt={label} width={size} height={size} /> : null}</span>
}

/** "Get the phone app" with the QR code to horizon.robotensor.ai/download (it switches to the APK or the iOS link by itself). */
export function GetTheApp({ size = 120, children }: { size?: number; children?: ReactNode }): ReactElement {
  return (
    <div className="w-getapp">
      <div className="w-getapp__text">
        <b>Get the phone app</b>
        <span>{children ?? 'Scan the code with your phone and sign in with the same account. Your phone becomes the controller.'}</span>
      </div>
      <div className="w-getapp__qr">
        <Qr value={APP_URL} size={size} label="QR code to horizon.robotensor.ai/download" />
        <small>horizon.robotensor.ai/download</small>
      </div>
    </div>
  )
}

/** Grey blocks the size of each card while it loads (no spinners). */
export function Block({ h = 80, className }: { h?: number; className?: string }): ReactElement {
  return <div className={`w-block${className ? ` ${className}` : ''}`} style={{ height: h }} aria-hidden />
}
